import React, { useEffect } from "react";
import Form from "react-bootstrap/Form";
import { useHistory, useLocation } from "react-router-dom";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import { routes } from "../../helper/routes";
import ExamList from "./ExamList";
import { sectionItems } from "./assessSectionData";
import { ExamProvider, useExamContext } from "../../context/Exam/ExamContext";
import { ExamGradingProvider } from "../../context/Exam/ExamGradingContext";
import "./AssessShared.css";
import "./ExamSection.css";
import MainButton from "../../components/common/MainButtonComponent";
import ExamConsentPage from "./ExamConsentPage";
import ExamPaperPage from "./ExamPaperPage";
import ExamGradeReportPage from "./ExamGradeReportPage";
import ExamFeedback from "./ExamFeedback";


function ExamSectionContent({ onIsExamModeChange }) {
  const {
    flow,
    items,
    loading,
    errorMessage,
    openGradeReport,
    openExamConsent,
    backToExamList,
    loadExamPaper,
    examPaper,
    examQuestions,
    examLoading,
    pendingExam,
    setPendingExam,
    candidateAnswers,
    examStartData,
    startOrResumeExam,
    tryAgainExam,
    loadMyExams,
    query,
    setExamViewMode,
    studentList,
    selectedStudentId,
    setSelectedStudentId,
  } = useExamContext();

  const location = useLocation();
  const examView = location.state?.examView || "new-exams";

  useEffect(() => {
    setExamViewMode(examView);
    loadMyExams(query, { current: true }, examView);
  }, [examView]);


  useEffect(() => {
    if (onIsExamModeChange) {
      onIsExamModeChange(
        flow.view === "consent" ||
        flow.view === "paper" ||
        flow.view === "feedback"
      );
    }

    return () => {
      if (onIsExamModeChange) {
        onIsExamModeChange(false);
      }
    };
  }, [flow, onIsExamModeChange]);

  const history = useHistory();
  const confirmExamRedirect = () => {
    if (pendingExam) {
      history.push(
        routes.exm_consent
          .replace(":id", pendingExam.item.id)
          .replace(":mode", pendingExam.mode)
      );
    }

    setPendingExam(null);
  };

  const handleFeedbackClose = ({ action } = {}) => {
    const exam = flow.selectedExam;

    if (action !== "success" || !exam?.candidate_id) {
      backToExamList();
      return;
    }

    history.replace({
      pathname: routes.exm_grade_report.replace(":id", exam.candidate_id),
      search: `?assessment_id=${exam.assessment_id || exam.id}&attempt_no=${exam.attempt_no || 1}`,
      state: { exam },
    });
  };

  if (flow.view === "consent") {
    return (
      <ExamConsentPage
        exam={flow.selectedExam}
        mode={flow.mode}
        loading={examLoading}
        errorMessage={errorMessage}
        onBack={backToExamList}
        onProceed={() => startOrResumeExam(flow.selectedExam, flow.mode)}
      />
    );
  }

  if (flow.view === "paper") {
    return (
      <ExamPaperPage
        examPaper={examPaper}
        questions={examQuestions}
        candidateAnswers={candidateAnswers}
        examStartData={examStartData}
        onBack={backToExamList}
      />
    );
  }

  if (flow.view === "feedback") {
    return (
      <ExamFeedback
        onClose={handleFeedbackClose}
        assessmentID={
          flow.selectedExam?.assessment_id || flow.selectedExam?.id
        }
        candidate_id={flow.selectedExam?.candidate_id}
        examSummary={flow.selectedExam?.feedbackSummary}
      />
    );
  }

  if (flow.view === "grade-report") {
    return (
      <ExamGradeReportPage
        exam={flow.selectedExam}
        onBack={backToExamList}
      />
    );
  }

  if (flow.view === "submit-success") {
    const hasMoreAttempts = flow.selectedExam?.hasMoreAttempts;

    return (
      <ContentWrapper>
        <section className="assess-section exam-section">
          <h2>Exam Submitted Successfully</h2>
          <p>{flow.selectedExam?.name || flow.selectedExam?.title}</p>

          <div className="exam-consent-actions">
            <MainButton
              action="view"
              icon="view"
              label="Grade Report"
              tone="success"
              onClick={() => openGradeReport(flow.selectedExam)}
            />

            {hasMoreAttempts && (
              <MainButton
                action="submit"
                icon="submit"
                label="Try Again"
                onClick={tryAgainExam}
              />
            )}

            <MainButton
              action="cancel"
              icon="cancel"
              label="Back to Exams"
              tone="neutral"
              onClick={backToExamList}
            />
          </div>
        </section>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <div className="exams-new-page">
        <div className="exams-header">
          <div className="exams-header-left">
            <h1 className="exams-title">Exams</h1>
            <p className="exams-subtitle">Manage active registrations and grade reports</p>
          </div>
          <div className="exams-header-right">
          </div>
        </div>

        {sessionStorage.getItem("userType") === "parent" && (
          <div className="student-select-wrap mb-3">
            <Form.Select className="w-50"
              aria-label="Select the student"
              name="student_id"
              value={selectedStudentId || ""}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedStudentId(value ? value : null);
              }}
            >
              <option value="">Select Student</option>
              {studentList.map((student) => (
                <option
                  key={student.student_user_table_id}
                  value={student.student_user_table_id}
                >
                  {`${student.first_name} ${student.last_name}`}
                </option>
              ))}
            </Form.Select>
          </div>
        )}

        {/* {loading && <p>Loading exams...</p>} */}
        {errorMessage && <p className="text-danger">{errorMessage}</p>}

        <ExamList
          items={items}
          viewMode={examView}
          onGradeReport={openGradeReport}
          onExamConsent={openExamConsent}
        />
      </div>
    </ContentWrapper>
  );
}

export default function ExamSection(props) {
  return (
    <ExamProvider>
      <ExamGradingProvider>
        <ExamSectionContent onIsExamModeChange={props.setIsExamMode} />
      </ExamGradingProvider>
    </ExamProvider>
  );
}
